/**
 * MobileLegalModal.jsx
 * Modal für AGB/Datenschutz-Akzeptierung in der Mobile App Preview
 */
import React, { useState } from 'react';
import { FileText, Shield, ExternalLink, CheckSquare, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const MobileLegalModal = ({ legalStatus, onAccept }) => {
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [customAcceptances, setCustomAcceptances] = useState({});
  const [loading, setLoading] = useState(false);

  const toggleCustomAcceptance = (conditionId) => {
    setCustomAcceptances(prev => ({
      ...prev,
      [conditionId]: !prev[conditionId]
    }));
  };

  const allRequiredAccepted = () => {
    if (!acceptedTerms || !acceptedPrivacy) return false;
    
    // Check all required custom conditions
    for (const condition of (legalStatus.custom_conditions || [])) {
      if (condition.required && !customAcceptances[condition.id]) {
        return false;
      }
    }
    return true;
  };

  const handleAccept = async () => {
    if (!allRequiredAccepted()) return;
    
    setLoading(true);
    const acceptedCustomIds = Object.keys(customAcceptances).filter(id => customAcceptances[id]);
    await onAccept(acceptedCustomIds);
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto border border-zinc-700">
        {/* Header */}
        <div className="sticky top-0 bg-zinc-900 p-4 border-b border-zinc-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
              <FileText className="w-5 h-5 text-yellow-500" />
            </div>
            <div>
              <h2 className="text-white font-semibold">Nutzungsbedingungen</h2>
              <p className="text-zinc-400 text-sm">Bitte akzeptieren Sie die Bedingungen</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* AGB */}
          <div 
            onClick={() => setAcceptedTerms(!acceptedTerms)}
            className={`p-4 rounded-xl border cursor-pointer transition-colors ${
              acceptedTerms ? 'bg-yellow-500/10 border-yellow-500/50' : 'bg-zinc-800/50 border-zinc-700'
            }`}
          >
            <div className="flex items-start gap-3">
              <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                acceptedTerms ? 'bg-yellow-500 border-yellow-500' : 'border-zinc-600'
              }`}>
                {acceptedTerms && <CheckSquare className="w-4 h-4 text-black" />}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-yellow-500" />
                  <span className="text-white font-medium">AGB akzeptieren *</span>
                </div>
                <p className="text-zinc-400 text-xs mt-1">
                  Ich habe die Allgemeinen Geschäftsbedingungen gelesen und akzeptiere diese.
                </p>
                {legalStatus.agb_url && (
                  <a 
                    href={legalStatus.agb_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-yellow-500 text-xs mt-2 flex items-center gap-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    AGB lesen <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Datenschutz */}
          <div 
            onClick={() => setAcceptedPrivacy(!acceptedPrivacy)}
            className={`p-4 rounded-xl border cursor-pointer transition-colors ${
              acceptedPrivacy ? 'bg-blue-500/10 border-blue-500/50' : 'bg-zinc-800/50 border-zinc-700'
            }`}
          >
            <div className="flex items-start gap-3">
              <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                acceptedPrivacy ? 'bg-blue-500 border-blue-500' : 'border-zinc-600'
              }`}>
                {acceptedPrivacy && <CheckSquare className="w-4 h-4 text-white" />}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-blue-400" />
                  <span className="text-white font-medium">Datenschutz akzeptieren *</span>
                </div>
                <p className="text-zinc-400 text-xs mt-1">
                  Ich habe die Datenschutzerklärung gelesen und stimme der Verarbeitung zu.
                </p>
                {legalStatus.privacy_url && (
                  <a 
                    href={legalStatus.privacy_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-400 text-xs mt-2 flex items-center gap-1"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Datenschutz lesen <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Custom Conditions */}
          {legalStatus.custom_conditions?.length > 0 && (
            <>
              <div className="border-t border-zinc-700 pt-4 mt-4">
                <p className="text-zinc-500 text-xs mb-3">Zusätzliche Bedingungen:</p>
              </div>
              
              {legalStatus.custom_conditions.map((condition) => (
                <div 
                  key={condition.id}
                  onClick={() => toggleCustomAcceptance(condition.id)}
                  className={`p-4 rounded-xl border cursor-pointer transition-colors ${
                    customAcceptances[condition.id] ? 'bg-green-500/10 border-green-500/50' : 'bg-zinc-800/50 border-zinc-700'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                      customAcceptances[condition.id] ? 'bg-green-500 border-green-500' : 'border-zinc-600'
                    }`}>
                      {customAcceptances[condition.id] && <CheckSquare className="w-4 h-4 text-white" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <CheckSquare className="w-4 h-4 text-green-400" />
                        <span className="text-white font-medium">
                          {condition.title} {condition.required && '*'}
                        </span>
                      </div>
                      {condition.description && (
                        <p className="text-zinc-400 text-xs mt-1">{condition.description}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}

          {/* Required Note */}
          <p className="text-zinc-500 text-xs text-center">* Pflichtfeld</p>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-zinc-900 p-4 border-t border-zinc-700">
          <button
            onClick={handleAccept}
            disabled={!allRequiredAccepted() || loading}
            className={`w-full py-3 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 ${
              allRequiredAccepted() && !loading
                ? 'bg-yellow-500 text-black'
                : 'bg-zinc-700 text-zinc-400 cursor-not-allowed'
            }`}
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Wird gespeichert...
              </>
            ) : (
              'Akzeptieren und fortfahren'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileLegalModal;
